import React from 'react';
import './Projects.css';

const ProjectItem = ({ icon, title, category, description, technologies, expertiseBy }) => (
  <div className="project-item">
    {icon && <div className="project-icon">{icon}</div>}
    {category && <span className="project-category">{category}</span>}
    <h3>{title}</h3>
    <p>{description}</p>
    {expertiseBy && (
      <p className="project-contributor">
        <span className="contributor-label">Expertise brought by:</span> {expertiseBy}
      </p>
    )}
    <div className="project-technologies">
      {technologies.map((tech, index) => (
        <span key={index} className="tech-tag">{tech}</span>
      ))}
    </div>
  </div>
);

const Projects = () => {
  const innodataProjects = [
    {
      icon: '🚨',
      title: 'EDaaG (Emergency Department At A Glance)',
      category: 'Healthcare · Workflow',
      description:
        'A mission-critical, 24x7 platform for orchestrating hospital emergency department workflows, engineered for low-to-zero downtime. It is a core enabler for the many clinicians and staff who keep the emergency department running efficiently, giving them a real-time view of the patient journey from arrival to diagnosis. A colour-coded escalation system (Green–Amber–Red–Black) signals ED pressure levels based on patient numbers, acuity, and waiting times.',
      technologies: ['.NET', 'SQL Server', 'Webforms', 'Entity Framework', 'Health New Zealand'],
    },
    {
      icon: '🔌',
      title: 'EDaaG Integration API',
      category: 'Healthcare · Integration',
      description:
        'The critical integration backbone powering bidirectional HL7 messaging for EDaaG, handling inbound Laboratory and Radiology orders and outbound Radiology order submission. Delivered for Health New Zealand, with Canterbury as the first adapter region, followed by Nelson Marlborough and the rest of the South Island — a foundation platform positioned to drive more mature, wider-reaching integrations as a key part of the future roadmap.',
      technologies: ['HL7', 'REST API', 'Health New Zealand', '.NET Core', 'EF Core', 'SQL Server'],
    },
    {
      icon: '🖥️',
      title: 'FloView',
      category: 'Healthcare · Patient Flow',
      description:
        'An online clinical patient management and care coordination system used within the Canterbury health system (Health New Zealand | Waitaha Canterbury, formerly CDHB). FloView acts as a digital electronic whiteboard and patient-tracking platform designed to improve clinical workflow and patient flow throughout hospital wards. It gives nurses, coordinators, and multidisciplinary teams (MDT) a real-time overview of patients arriving at or admitted to a ward; details which health professionals a patient has seen and who they are still scheduled to see; surfaces critical inpatient specifics such as MDT referrals, dietary codes, and food texture data; and sends alerts for patients with complex medical or psychosocial support plans.',
      technologies: ['Health New Zealand', 'Canterbury', 'HL7'],
    },
  ];

  const teamExpertise = [
    {
      icon: '📨',
      title: 'eReferral (ERMS)',
      category: 'Practitioner Expertise',
      description:
        'Prior expertise brought to Innodata by our engineer Red Mati, who spent nearly a decade at Pegasus Health (2011–2021) progressing from Senior Developer through to Technical Lead and Product Manager on the South Island eReferral Management System. The platform spans all five South Island regions - Canterbury, South Canterbury, Southern, Nelson-Marlborough, and West Coast - streamlining specialist referrals and improving care coordination between primary and secondary health services. Red led bidirectional HL7 and FHIR integrations with external providers including COMRAD and Pacific Radiology, and managed a full product team of developers, testers, and a business analyst to drive strategy, delivery, and go-to-market execution.',
      technologies: ['HL7', 'FHIR', '.NET Core', 'eReferral', 'South Island', 'Health NZ'],
      expertiseBy: 'Red Mati',
    },
    {
      icon: '🏢',
      title: 'Insurance Sales Platform',
      category: 'Practitioner Expertise',
      description:
        'Prior expertise brought to Innodata by our engineer Red Mati, who served as Regional IT Consultant at ACE Insurance Asia Pacific (Singapore), where he led the design and delivery of a bespoke C#.NET/ASP.NET regional call centre and sales platform deployed across the APAC region. The platform transformed manual insurance offices into integrated digital hubs, driving an 80–90% increase in regional sales productivity. Red implemented high-availability infrastructure via SQL Mirroring and load balancing, achieving 99% system uptime, and led the rollout of an automated AVAYA dialing solution that scaled the telemarketing operation from 250 to 500 staff to meet surging demand.',
      technologies: ['C#.NET', 'ASP.NET', 'SQL Server', 'AVAYA', 'APAC'],
      expertiseBy: 'Red Mati',
    },
    {
      icon: '🏡',
      title: 'Aged-care Health – Resident Experience Programme',
      category: 'Practitioner Expertise',
      description:
        'Prior expertise brought to Innodata by our engineer Red Mati, who led a multidisciplinary team of engineers, web developers, mobile developers, and QAs at a leading New Zealand aged-care provider (Christchurch) on a high-stakes Resident Experience Programme. The programme delivered integrated web and mobile app experiences for aged care residents, with a strong focus on code quality, CI/CD discipline, and sustainable delivery culture. Red served as a combined Team Lead and Scrum Master, bridging product roadmaps through to hands-on release management.',
      technologies: ['.NET Core', 'React JS', 'React Native', 'CI/CD', 'Aged Care'],
      expertiseBy: 'Red Mati',
    },
    {
      icon: '🛡️',
      title: 'ePROSAFE',
      category: 'Practitioner Expertise',
      description:
        'Prior expertise brought to Innodata by our engineer Raquel Mati, who has extensive hands-on experience with ePROSAFE, a specialised web-based application originally developed by the Canterbury District Health Board (now Health New Zealand | Te Whatu Ora) to improve clinical auditing, risk assessment, and regional information-sharing for child protection and family violence cases. The system is split into two primary clinical areas: Child Protection and Family Violence. It provides clinicians a centralised space to record personal details, risk assessments, safety plan information, agencies involved, and actions taken. ePROSAFE enables secure cross-agency and cross-district referral management, replacing fragmented paper-based processes, and applies strict access controls - including a restricted child protection module accessible only to authorised personnel and external partner agencies such as the New Zealand Police.',
      technologies: ['Child Protection', 'Family Violence', 'Health NZ', 'Canterbury'],
      expertiseBy: 'Raquel Mati',
    },
    {
      icon: '🏥',
      title: 'SingHealth – DOT Chronic Disease Management',
      category: 'Practitioner Expertise',
      description:
        'Prior expertise brought to Innodata by our engineer Raquel Mati, who worked as IT System Specialist at IHIS Pte Ltd (Integrated Health Information System, formerly SingHealth) on the Singapore Ministry of Health\'s "Delivering on Target" (DOT) programme. The DOT system is a government-funded web-based platform designed to manage patients with chronic diseases who are clinically suitable for discharge from specialist outpatient clinics and transition to primary care management by GPs and polyclinics. Raquel contributed across the full delivery lifecycle - from data gathering and database design through to GUI development and production server configuration - serving clients including Singapore General Hospital\'s Diabetes Centre, the National Heart Centre, and Changi General Hospital.',
      technologies: ['ASP.NET', 'VB.NET', 'MS SQL', 'Ministry of Health', 'Singapore', 'SingHealth'],
      expertiseBy: 'Raquel Mati',
    },
  ];

  return (
    <section id="projects" className="section projects-section">
      <div className="section-content">
        <h1>Projects, Portfolio &amp; Expertise</h1>
        <p className="subtitle">A selection of our recent work and the deep domain knowledge our team brings.</p>

        <h2 className="projects-subheading">Innodata Projects</h2>
        <div className="projects-grid">
          {innodataProjects.map((project, index) => (
            <ProjectItem
              key={index}
              icon={project.icon}
              title={project.title}
              category={project.category}
              description={project.description}
              technologies={project.technologies}
            />
          ))}
        </div>

        <div className="projects-divider" />

        <h2 className="projects-subheading">Team Expertise</h2>
        <p className="projects-subheading-note">
          The following reflects deep hands-on experience our engineers bring from prior roles - not projects delivered directly by Innodata.
        </p>
        <div className="projects-grid">
          {teamExpertise.map((item, index) => (
            <ProjectItem
              key={index}
              icon={item.icon}
              title={item.title}
              category={item.category}
              description={item.description}
              technologies={item.technologies}
              expertiseBy={item.expertiseBy}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
